import React, { useState, useEffect, useRef } from 'react'
import { Layout, Grid, Heading, Button } from '@enterprise-ui/canvas-ui-react'
import {
  CancelIcon,
  CheckIcon,
  EnterpriseIcon,
  PencilIcon,
  PlusIcon,
} from '@enterprise-ui/icons'
import { AgGridReact } from 'ag-grid-react'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import { ExemptionVendorResponse } from '../../api/models/ExemptionVendor'
import { GridReadyEvent } from 'ag-grid-community'
import AutoSizer from 'react-virtualized-auto-sizer'
import { useAuth } from '@praxis/component-auth'
import { useExemptionVendor } from '../../api/hooks/useExemptionVendor'
import { ExemptionVendorViewWrapper } from './ExemptionVendorViewWrapper'
import moment from 'moment'
import { exportRowsToExcel } from '../Dashboard/AgGridExportToExcel'

export const ExemptionVendorList = () => {
  const [vendorList, setVendorList] = useState<ExemptionVendorResponse[]>([])
  const { getAllVendors } = useExemptionVendor()
  const { isAuthenticated } = useAuth()
  const [showCreateScreen, setShowCreateScreen] = useState<boolean>(false)
  const [reloadData, setReloadData] = useState<boolean>(true)
  const [exemptionId, setExemptionId] = useState(0)
  const [isGridReady, setIsGridReady] = useState<boolean>(false)
  const gridApi = useRef<any>(null)

  useEffect(() => {
    if (isAuthenticated() && (reloadData || vendorList.length === 0)) {
      getAllVendors()
        .then((response: any) => {
          if (response && response.exemptionVendorResponseList) {
            setVendorList(response.exemptionVendorResponseList)
          } else if (Array.isArray(response)) {
            setVendorList(response)
          } else if (response && Array.isArray(response.data)) {
            setVendorList(response.data)
          } else {
            setVendorList([])
          }
          setReloadData(false)
        })
        .catch((error: any) => {
          console.error('Error loading vendor exemption data:', error)
          setVendorList([])
          setReloadData(false)
        })
    } else if (!isAuthenticated()) {
      setVendorList([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadData, isAuthenticated])

  useEffect(() => {
    if (isAuthenticated() && vendorList.length === 0) {
      setReloadData(true)
    }
  }, [isAuthenticated])



  const handleExcelDownload = async () => {
    if (!gridApi.current || vendorList.length === 0) {
      return
    }
    try {
      await exportRowsToExcel(gridApi.current, {
        filename: 'vendor_exemption_list.xlsx',
        sheetName: 'Vendor Exemption List',
        onlySelected: false,
        onlyVisibleColumns: false,
        exportAllRows: false,
        excludeColumns: ['exemptionVendorId'],
      })
    } catch (error) {
      console.error('Failed to export to Excel:', error)
    }
  }



  return (
    <>
      <Layout.SectionHeader>
        <Grid.Container justify="space-between">
          <Grid.Item xs={2}>
            <Heading size={4}>Vendor Exemptions</Heading>
          </Grid.Item>
          <Grid.Item xs={7}></Grid.Item>
          <Grid.Item xs={3} style={{ textAlign: 'right' }}>
            <Button
              type="secondary"
              className="hc-mr-min"
              onClick={() => {
                setExemptionId(0)
                setShowCreateScreen(true)
              }}
            >
              <EnterpriseIcon icon={PlusIcon}></EnterpriseIcon>
              Add Vendors
            </Button>
            <Button
              type="secondary"
              className="hc-mr-min"
              onClick={handleExcelDownload}
              disabled={!isGridReady || vendorList.length === 0}
              style={{ marginLeft: 8 }}
              title={!isGridReady ? 'Grid is not ready' : vendorList.length === 0 ? 'No data to export' : `Export ${vendorList.length} vendors`}
            >
              <EnterpriseIcon icon={PlusIcon} />
              Export to Excel
            </Button>
          </Grid.Item>
        </Grid.Container>
        <Grid.Container>
          <Grid.Item xs={12}>
            <div style={{ height: '70vh', width: '100%', marginTop: '10px' }}>
              <AutoSizer>
                {({ height, width }: { height: number; width: number }) => (
                  <div style={{ height, width }}>
                    <AgGridReact
                      className="ag-theme-alpine"
                      rowData={vendorList}
                      columnDefs={[
                  {
                    field: 'exemptionVendorId',
                    headerName: 'Action',
                    width: 80,
                    pinned: 'left',
                    cellRenderer: ({ value }: any) => (
                      <Button
                        iconOnly
                        aria-label="Edit"
                        onClick={() => {
                          setShowCreateScreen(true)
                          setExemptionId(value)
                        }}
                        style={{ border: 'none', background: 'transparent', padding: '4px' }}
                      >
                        <EnterpriseIcon icon={PencilIcon} size="sm" />
                      </Button>
                    ),
                  },
                  {
                    field: 'vendorNumber',
                    headerName: 'Vendor Number',
                    width: 130,
                    sortable: true,
                    filter: true,
                  },
                  {
                    field: 'vendorName',
                    headerName: 'Vendor Name',
                    width: 250,
                    flex: 1,
                    sortable: true,
                    filter: true,
                  },
                  {
                    field: 'active',
                    headerName: 'Active',
                    width: 80,
                    cellRenderer: ({ value }: any) => (
                      value ? (
                        <EnterpriseIcon icon={CheckIcon} color="#28a745" size="sm" />
                      ) : (
                        <EnterpriseIcon icon={CancelIcon} color="#dc3545" size="sm" />
                      )
                    ),
                  },
                  {
                    field: 'permanentExemptFlag',
                    headerName: 'Permanent Exemption',
                    width: 180,
                    cellRenderer: ({ value }: any) => (
                      value ? (
                        <EnterpriseIcon icon={CheckIcon} color="#28a745" size="sm" />
                      ) : (
                        <EnterpriseIcon icon={CancelIcon} color="#dc3545" size="sm" />
                      )
                    ),
                  },
                  {
                    field: 'exemptStartDate',
                    headerName: 'Exemption Start Date',
                    width: 180,
                    valueFormatter: (params: any) => 
                      params.value ? moment(params.value).format('MM/DD/YYYY') : '',
                  },
                  {
                    field: 'exemptEndDate',
                    headerName: 'Exemption End Date',
                    width: 170,
                    valueFormatter: (params: any) => 
                      params.value ? moment(params.value).format('MM/DD/YYYY') : '',
                  },
                  {
                    field: 'ediStatus',
                    headerName: 'Edi Status',
                    width: 100,
                  },
                  {
                    field: 'updateUser',
                    headerName: 'Last Modified By',
                    width: 150,
                  },
                  {
                    field: 'updateTimestamp',
                    headerName: 'Last Modified Date',
                    width: 160,
                    valueFormatter: (params: any) => 
                      params.value ? moment(params.value).format('MM/DD/YYYY') : '',
                  },
                  {
                    field: 'comment',
                    headerName: 'Comment',
                    width: 200,
                    flex: 1,
                  },
                ]}
                defaultColDef={{
                  sortable: true,
                  filter: true,
                  resizable: true,
                }}
                pagination={true}
                paginationPageSize={100}
                onGridReady={(params: GridReadyEvent) => {
                  gridApi.current = params.api
                  setIsGridReady(true)
                  params.api.sizeColumnsToFit()
                }}
                rowHeight={40}
                headerHeight={35}
                animateRows={true}
                enableCellTextSelection={true}
                suppressMenuHide={false}
                domLayout="normal"
              />
                  </div>
                )}
              </AutoSizer>            
            </div>
          </Grid.Item>
        </Grid.Container>
        <ExemptionVendorViewWrapper
          showCreateScreen={showCreateScreen}
          setShowCreateScreen={setShowCreateScreen}
          setReloadData={setReloadData}
          exemptionId={exemptionId}
        />
      </Layout.SectionHeader>
    </>
  )
}
